<script lang="ts">
  /**
   * A solo run: the engine state, the fixed-timestep loop, the renderer, the
   * HUD and the end of the run, in the shell's `GameFrame`
   * (`docs/decisions/0054`). The frame is the chrome; the board (the canvas
   * and its rail, painted by the board look, dark or light with the
   * appearance) and the touch pads (in
   * the frame's free-form `bottom`, in the owner's arrangement) stay the
   * game's. Everything it owns is created in `onMount` and torn down in the
   * cleanup `onMount` returns, so leaving this screen (quit, or a result)
   * never leaks a `requestAnimationFrame` loop or a set of DOM listeners.
   * What it shares with the duel screen (input, lock feedback, the wake lock
   * and visibility) lives in `src/play/`.
   *
   * Driven entirely by the `@interlock/modes` registry: `startMode` creates
   * the game and runs `setup`, and `mode.onTick`/`checkEnd`/`hud` are called
   * the same way for every mode. Nothing here branches on `modeId`.
   *
   * ## The frame
   *
   * On a phone one column: the bar (the menu button, the mode's readouts in
   * one bold line over the mode and its configuration, and the personal best
   * this run is up against at the right), the board, the pads. From 1024 px
   * the frame's `split`: the board the full height on the left, the bar and
   * the keys (`side`) down a panel on the right; the pads show there only
   * where the pointer is a finger. A phone on its side (`landscapeSplit`,
   * shell 1.5.0) gets the same idea below 1024 px: the board the full height
   * on the left, the bar and the pads down a panel on the right, so the board
   * is not squeezed between them (`App.svelte` lifts the column for it,
   * `docs/decisions/0057`). The readouts are `ModeDescriptor.hud`
   * evaluated against the current game, written at a throttled rate
   * (`docs/KNOWN-BUGS.md` item 5); nothing in the bar branches per mode.
   *
   * ## Paused while something is open over it
   *
   * The run is paused while an overlay covers it: Settings or How to play
   * (the `paused` prop), the Game menu, or this screen's own "Quit this
   * run?" question. A solo run is the player's alone, so it simply stops:
   * the loop (and with it the effects, which only advance on a tick), input
   * (held keys are released and presses are dropped rather than buffered)
   * and the music's scheduler. Closing the overlay starts all of it again
   * from exactly where it was, with no catch-up: `GameLoop.start` forgets the
   * last frame time. `data-paused` on the root is the e2e-observable form of
   * the same fact.
   *
   * ## Back asks before it quits
   *
   * This screen is an overlay entry `App.svelte` pushed. While the run is in
   * progress (which is for as long as this component is mounted: a finished
   * run leaves for the results screen in the same tick) `GameFrame`'s run
   * guard is stacked on that entry, so Back asks "Quit this run?" instead of
   * throwing the run away. The Game menu's "Quit run" asks the same question
   * (`requestLeave()`). The frame says when the question opens and closes
   * (`guard.onasking`, shell 1.3.0) and the run pauses meanwhile; `live`
   * stays true throughout, because `live` turning false would withdraw the
   * question (`docs/decisions/0055`).
   *
   * ## A turn on one device (`docs/decisions/0059`)
   *
   * With `turn`, the run is one player's turn of a round taken in turns on
   * one device (`screens/LocalRoundScreen.svelte`): the same run on the
   * round's seed, the player's name in the bar where the personal best would
   * be, and the end handed to `turn.onEnd` **instead of being recorded**:
   * no personal best, no solo statistic, no daily, no account push. Quitting
   * asks "End {name}'s turn?" and counts the turn as quit; the round goes on
   * with the next player.
   */
  import { onMount, tick, untrack } from 'svelte';

  import { Chip, GameFrame, Group, Row, SectionLabel } from '@artibens/app-shell';
  import type { GameGuard } from '@artibens/app-shell';

  import { hashStateHex } from '@interlock/engine';
  import type { InputAction } from '@interlock/engine';
  import { buildMode, describeModeConfig, encodeModeConfig, startMode } from '@interlock/modes';
  import type { ModeConfig, ModeOutcome, PersonalBestScore } from '@interlock/modes';
  import { boardTheme } from '@interlock/themes';

  import { audio } from '../audio/engine.js';
  import { boardDangerFraction } from '../audio/danger.js';
  import { KEY_HINTS } from '../copy/keys.js';
  import { GameLoop } from '../loop/game-loop.js';
  import { EffectsController } from '../render/effects.js';
  import { Canvas2DRenderer } from '../render/canvas2d-renderer.js';
  import { ThrottledWriter } from '../hud/throttle.js';
  import { fitToStage } from '../play/fit-stage.js';
  import { playLockFeedback } from '../play/lock-feedback.js';
  import { PlayInput } from '../play/play-input.svelte.js';
  import { startPlayScreen } from '../play/play-screen.js';
  import GameMenu from '../nav/GameMenu.svelte';
  import { returnKeysToBoard } from '../nav/focus-on-show.js';
  import { syncBest, syncStats } from '../state/account.svelte.js';
  import { postDailyResult } from '../state/daily.svelte.js';
  import type { PersonalBestRecord } from '../state/personal-bests-io.js';
  import { loadPersonalBest, recordPersonalBest } from '../state/personal-bests-io.js';
  import { settings } from '../state/settings.svelte.js';
  import { recordGameFinish, recordGameStart } from '../state/solo-stats-io.js';
  import { turnOutcomeFor } from '../local/round.js';
  import type { TurnResult } from '../local/round.js';
  import TouchControls from './TouchControls.svelte';

  /** One player's turn of a round on one device. */
  interface LocalTurn {
    /** Whose turn it is. */
    readonly player: string;
    /** The round's seed (`local/round.ts#seedForTurn`). */
    readonly seed: number;
    /** Who plays next, or `null` when this is the last turn. */
    readonly next: string | null;
    /** The turn is over: finished, topped out, or quit from the question. */
    readonly onEnd: (result: TurnResult) => void;
  }

  let {
    config,
    dailySeed = null,
    turn = null,
    onFinish,
    onQuit,
    onSettings,
    onHowToPlay,
    paused = false,
  }: {
    config: ModeConfig;
    /**
     * The server-assigned seed for today's daily, or `null` for an ordinary
     * solo run. When set, the run uses it in place of a seed from the clock,
     * and a genuine finish (a win, never a top-out) is posted to the server
     * without waiting for the answer.
     */
    dailySeed?: number | null;
    /**
     * A turn of a round on one device, or `null` for a solo run. See this
     * file's header: a turn records nothing anywhere.
     */
    turn?: LocalTurn | null;
    onFinish: (
      outcome: ModeOutcome,
      best: { best: PersonalBestRecord; isNewBest: boolean } | null,
      /** This run's own figure, or `null` when the mode chose not to score it. */
      score: PersonalBestScore | null,
    ) => void;
    onQuit: () => void;
    /** Opens Settings. */
    onSettings: () => void;
    /** Opens How to play. */
    onHowToPlay: () => void;
    /** True while a page of the app's is open over the run: see this file's header. */
    paused?: boolean;
  } = $props();

  /** True while "Quit this run?" is on screen. Written only by the frame's guard. */
  let asking = $state(false);
  /**
   * The run guard: live for the life of the component (a finished run leaves
   * this screen in the same tick), and given back by the frame however the
   * screen goes. "Keep playing" hands the keys back to the board through the
   * effect below, which runs whenever the run stops being halted.
   */
  const localTurn = untrack(() => turn);
  /** How far the run has got, for a turn that is quit; set once the run starts. */
  let progress: (() => { lines: number; timeMs: number }) | null = null;
  const guard: GameGuard = {
    live: true,
    ...(localTurn === null
      ? {
          title: 'Quit this run?',
          body: 'The run ends here and nothing from it is saved.',
          confirmLabel: 'Quit',
        }
      : {
          title: `End ${localTurn.player}'s turn?`,
          body:
            localTurn.next === null
              ? `${localTurn.player}'s turn counts as quit, and the round ends with the results.`
              : `${localTurn.player}'s turn counts as quit, and the round goes on with ${localTurn.next}.`,
          confirmLabel: 'End turn',
        }),
    cancelLabel: 'Keep playing',
    onleave: () => {
      if (localTurn === null) {
        onQuit();
        return;
      }
      localTurn.onEnd({ outcome: 'quit', ...(progress?.() ?? { lines: 0, timeMs: 0 }) });
    },
    onasking: (next) => {
      asking = next;
    },
    testid: 'quit-confirm',
  };
  /** The frame, for its `requestLeave()`. */
  let frame: ReturnType<typeof GameFrame> | undefined = $state();

  let rootEl: HTMLDivElement | undefined = $state();

  /** True while the Game menu is open. */
  let menuOpen = $state(false);

  /** The run stops for every overlay: the app's page, the Game menu, or this screen's own question. */
  const halted = $derived(paused || asking || menuOpen);

  // Whatever was open over the run has closed. The shell hands focus back to
  // whatever opened it (the menu button), and a focused button would take the
  // next Space instead of the board.
  $effect(() => {
    if (halted) return;
    void tick().then(() => {
      returnKeysToBoard(rootEl);
    });
  });

  // The only place `config` itself is consulted: to build the descriptor.
  // Everything after this reads the `ModeDescriptor`
  // (`docs/decisions/0021`).
  const mode = $derived(buildMode(config));

  /** The personal best this run is up against, read once: the bar's chip. */
  const bestAtStart = untrack(() =>
    // A turn on one device is not up against the device owner's best.
    localTurn === null ? loadPersonalBest(encodeModeConfig(config)) : null,
  );

  let stageEl: HTMLDivElement | undefined = $state();
  let canvasEl: HTMLCanvasElement | undefined = $state();
  let hudFields: readonly { id: string; label: string; value: string }[] = $state([]);
  /**
   * A test-observable counter, not a game feature: e2e cannot read canvas
   * pixels, so this is what `e2e/audio-effects.spec.ts` asserts against
   * instead. It ticks once per clear burst, on the DOM as `data-clear-fx`.
   */
  let clearFxCount = $state(0);

  const input = new PlayInput();

  /** What `onMount` exposes to the effects below: the run's own start/stop, and a one-off repaint. */
  interface RunControl {
    setPaused(paused: boolean): void;
    repaint(): void;
  }
  let runControl: RunControl | undefined = $state.raw();

  $effect(() => {
    runControl?.setPaused(halted);
  });

  // A paused run draws no frames, so a look changed behind the sheet (the
  // appearance, which the board follows, the ghost, high contrast) is
  // painted once, by hand.
  $effect(() => {
    void settings.appearance;
    void settings.ghost;
    void settings.highContrast;
    if (halted) runControl?.repaint();
  });

  onMount(() => {
    if (!stageEl || !canvasEl) return undefined;

    // A daily run's seed comes from the server, so every player who opens
    // today's daily gets the identical board. An ordinary solo run seeds
    // itself from the clock.
    // A turn on one device plays the round's seed, whoever's turn it is.
    const seed = localTurn?.seed ?? dailySeed ?? Date.now() >>> 0;
    const { state, runtime } = startMode(mode, seed);
    progress = () => ({ lines: state.lines, timeMs: runtime.elapsedMs });
    // A run has begun: counted here, once, not when Start was pressed. A turn
    // on one device is not a solo run, and is never counted.
    if (localTurn === null) recordGameStart(config.modeId);
    const evaluateHud = (): { id: string; label: string; value: string }[] =>
      mode.hud.map((readout) => ({
        id: readout.id,
        label: readout.label,
        value: readout.select(state, runtime),
      }));
    hudFields = evaluateHud();

    const effects = new EffectsController();
    const renderer = new Canvas2DRenderer(canvasEl);
    const hudWriter = new ThrottledWriter<readonly { id: string; label: string; value: string }[]>(
      100,
      (value) => {
        hudFields = value;
      },
    );

    const session = input.start();

    function resize(): void {
      if (stageEl) fitToStage(stageEl, renderer, state.board);
    }

    let finished = false;

    // Captured from `collectActions` (called immediately before the tick's
    // step: `game-loop.ts`'s own frame loop) so `onTick` can tell a
    // hard-drop lock from an ordinary lock-delay one without the engine
    // reporting anything new: `LockResult` alone cannot distinguish them.
    let lastActions: readonly InputAction[] = [];

    const loop = new GameLoop(state, {
      // `exactOptionalPropertyTypes`: the key is spread in only when the mode
      // has a rule, because an explicit `undefined` is not an omitted key.
      ...(mode.tickRule ? { tickRule: mode.tickRule } : {}),
      collectActions: () => {
        lastActions = session.collectActions();
        return lastActions;
      },
      onTick: (result, tickMs) => {
        mode.onTick?.(state, runtime, tickMs, result);

        clearFxCount += playLockFeedback({
          locks: result.locks,
          actions: lastActions,
          effects,
          boardWidth: state.board.width,
        });
        audio.setDanger(boardDangerFraction(state.board));

        effects.advance(tickMs);
        hudWriter.update(evaluateHud(), runtime.elapsedMs);

        if (finished) return;
        const outcome = mode.checkEnd(state, runtime);
        if (outcome) {
          finished = true;
          loop.stop();
          playScreen.releaseWakeLock();
          audio.playSfx(outcome.result === 'lose' ? 'lose' : 'win');
          // A turn on one device ends here: its facts go to the round, and
          // nothing below (a best, a statistic, the daily, the account) runs.
          if (localTurn !== null) {
            localTurn.onEnd({
              outcome: turnOutcomeFor(config, outcome),
              lines: state.lines,
              timeMs: runtime.elapsedMs,
            });
            return;
          }
          // `mode.score` is the mode's own read on whether (and how) this
          // run counts against the personal-bests store (sprint's lines
          // goal returns `null` on a loss: nothing to compare); recording
          // stays generic here regardless of what that decision was.
          const score = mode.score?.(state, runtime, outcome) ?? null;
          const modeKey = encodeModeConfig(config);
          const best = score ? recordPersonalBest(modeKey, score) : null;
          // The account, if there is one, is told *after* the local store has
          // answered; the results screen never waits for a network. For a
          // guest, an offline run or an unreachable server this does nothing.
          if (best !== null && best.isNewBest) syncBest(modeKey, best.best);
          // A daily run posts only on a genuine finish: `mode.score` is
          // `null` on a top-out. Not awaited; the results screen reads the
          // shared `daily` store when the answer lands.
          if (dailySeed !== null && score !== null) {
            void postDailyResult({
              seed: dailySeed,
              result: score.value,
              boardHash: hashStateHex(state),
              pieceCount: state.piecesPlaced,
            });
          }
          // A finished run, for the stats: any real outcome, as opposed to a
          // quit, which never reaches this branch.
          const modeStats = recordGameFinish(config.modeId, {
            lines: state.lines,
            pieces: state.piecesPlaced,
            timeMs: runtime.elapsedMs,
          });
          syncStats(config.modeId, modeStats);
          onFinish(outcome, best, score);
        }
      },
      onRender: renderFrame,
    });

    function renderFrame(): void {
      renderer.render({
        state,
        theme: boardTheme(settings.appearance),
        effects: effects.snapshot(),
        showGhost: settings.ghost,
        highContrast: settings.highContrast,
      });
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(stageEl);
    resize();

    // One place decides whether the run is running, from the three things
    // that can stop it: it finished, an overlay is open over it, or the page
    // is hidden.
    let overlayOpen = false;
    function syncRunning(): void {
      session.setSuspended(overlayOpen);
      const shouldRun = !finished && !overlayOpen && document.visibilityState !== 'hidden';
      if (shouldRun) {
        if (!loop.isRunning) loop.start();
        // Menus never start the music (`docs/decisions/0042`): only actual
        // play does. Idempotent, and it picks the song up where it stopped.
        audio.startMusic();
      } else {
        loop.stop();
        audio.stopMusic();
      }
    }

    const playScreen = startPlayScreen(syncRunning);
    syncRunning();
    runControl = {
      setPaused: (next) => {
        if (overlayOpen === next) return;
        overlayOpen = next;
        syncRunning();
      },
      repaint: renderFrame,
    };

    return () => {
      runControl = undefined;
      loop.stop();
      resizeObserver.disconnect();
      input.stop();
      playScreen.destroy();
    };
  });

  /** The Game menu's "Quit run": the same question Back asks. */
  function quit(): void {
    if (frame?.requestLeave() !== true) onQuit();
  }
</script>

{#snippet hud()}
  <span class="readout">
    {#each hudFields as field, index (field.id)}
      {#if index > 0}<span class="sep" aria-hidden="true">·</span>{/if}<span
        class="field"
        data-testid="hud-field"
        data-field={field.id}
        ><span class="field-label">{field.label}</span>
        <span class="field-value">{field.value}</span></span
      >
    {/each}
  </span>
  <span class="mode">
    <h1 class="mode-name">{mode.copy.name}</h1>
    <span aria-hidden="true">·</span>
    <span data-testid="hud-config">{describeModeConfig(config)}</span>
  </span>
{/snippet}

{#snippet best()}
  {#if localTurn !== null}
    <Chip tone="fill" testid="hud-player" title="Whose turn it is">{localTurn.player}</Chip>
  {:else if bestAtStart !== null}
    <Chip tone="fill" testid="hud-best" title="Your personal best for this configuration"
      >Best {bestAtStart.formatted}</Chip
    >
  {/if}
{/snippet}

{#snippet pads()}
  <TouchControls bind:root={input.touchRoot} />
{/snippet}

<!-- The keys, beside the board from 1024 px (the frame draws `side` only there). -->
{#snippet keys()}
  <SectionLabel variant="sheet" as="h2">Keys</SectionLabel>
  <Group name="Keys">
    {#each KEY_HINTS as row (row.action)}
      <Row label={row.action} value={row.keys} />
    {/each}
  </Group>
{/snippet}

<div
  class="game-screen"
  data-paused={halted}
  data-seed={localTurn?.seed}
  data-testid="game-screen"
  bind:this={rootEl}
>
  <GameFrame
    bind:this={frame}
    onmenu={() => {
      menuOpen = true;
    }}
    {hud}
    trailing={best}
    bottom={pads}
    side={keys}
    wide="split"
    landscapeSplit
    {guard}
    testids={{ menu: 'game-menu-open' }}
  >
    <div class="board">
      <div class="stage" bind:this={stageEl} data-clear-fx={clearFxCount} data-testid="game-stage">
        <canvas bind:this={canvasEl} data-testid="game-canvas"></canvas>
      </div>
    </div>
  </GameFrame>
</div>

<GameMenu
  open={menuOpen}
  onclose={() => {
    menuOpen = false;
  }}
  {onHowToPlay}
  {onSettings}
  exitTitle={localTurn === null ? 'Quit run' : 'End turn'}
  exitDetail={localTurn === null
    ? 'The run ends here and nothing is saved'
    : 'The turn counts as quit; the round goes on'}
  exitTestid="game-menu-quit"
  onExit={quit}
/>

<style>
  .game-screen {
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  /* The bold line: the mode's own readouts. The label is the quiet half of a
     readout; the number is the line. */
  .readout {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: var(--ui-text-heading);
    font-variant-numeric: tabular-nums;
    color: var(--ui-ink);
  }

  .field {
    white-space: nowrap;
  }

  .field-label {
    font: var(--ui-text-eyebrow);
    color: var(--ui-ink-2);
  }

  .sep {
    margin: 0 0.35em;
    color: var(--ui-ink-2);
  }

  /* The quiet line: the mode and its configuration. The mode's name is the
     screen's heading, set as part of the line. */
  .mode {
    display: inline-flex;
    align-items: center;
    gap: var(--ui-space-1);
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    font: var(--ui-text-caption);
    color: var(--ui-ink-2);
  }

  .mode-name {
    margin: 0;
    font: inherit;
    color: inherit;
  }

  /* The board: every pixel the frame's stage gives it, inset a little from
     the screen's edges. The stage itself has no padding: the canvas is
     fitted to its box (`play/fit-stage.ts`). */
  .board {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--ui-space-1) var(--ui-space-2) 0;
  }

  .stage {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    max-width: 44rem;
    display: flex;
    justify-content: center;
  }

  canvas {
    display: block;
  }
</style>
